import { Type } from '@sinclair/typebox';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
export const SkillScopeSchema = Type.Union([
    Type.Literal('deployment'),
    Type.Literal('track'),
    Type.Literal('user_track'),
]);
export const SkillStatusSchema = Type.Union([
    Type.Literal('approved'),
    Type.Literal('pending'),
]);
const SkillSlugSchema = Type.String({ minLength: 1, maxLength: 200 });
const SkillNameSchema = Type.String({ minLength: 1, maxLength: 200 });
const SkillDescriptionSchema = Type.String({ minLength: 1, maxLength: 2000 });
const SkillContentMarkdownSchema = Type.String({ minLength: 1, maxLength: 16 * 1024 });
const SkillCapabilitySchema = Type.String({ minLength: 1, maxLength: 200 });
const SkillCapabilitiesSchema = Type.Array(SkillCapabilitySchema, { maxItems: 100 });
/** Path params shared by personal skill management item routes. */
export const PersonalSkillIdParamsSchema = Type.Object({ id: UuidSchema }, { additionalProperties: false });
/**
 * Query for `GET /skills` in the normal-user personal management surface.
 * `trackId` is required so list semantics cannot drift into cross-track reads.
 */
export const PersonalSkillListQuerySchema = Type.Object({ trackId: UuidSchema }, { additionalProperties: false });
/**
 * Owner-management DTO for personal `user_track` skills.
 *
 * Full markdown is intentionally present here because this surface lets the
 * owner inspect/delete/request Copilot edits for their own personal skill
 * (COP-036/COP-064). Runtime loaded-skill SSE events remain metadata-only via
 * `SkillLoadedPartSchema` below.
 */
export const PersonalSkillRowSchema = Type.Object({
    id: UuidSchema,
    scope: Type.Literal('user_track'),
    trackId: UuidSchema,
    ownerUserId: UuidSchema,
    slug: SkillSlugSchema,
    name: SkillNameSchema,
    description: SkillDescriptionSchema,
    contentMarkdown: SkillContentMarkdownSchema,
    requiredCapabilities: SkillCapabilitiesSchema,
    optionalCapabilities: SkillCapabilitiesSchema,
    status: SkillStatusSchema,
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
}, { additionalProperties: false });
export const PersonalSkillListResponseSchema = Type.Object({
    skills: Type.Array(PersonalSkillRowSchema),
    total: Type.Integer({ minimum: 0 }),
}, { additionalProperties: false });
export const PersonalSkillGetResponseSchema = Type.Object({ skill: PersonalSkillRowSchema }, { additionalProperties: false });
/**
 * Query for `GET /skills/mine` — the Copilot-widget skills-management dropdown.
 * The widget holds only the `sessionId` (it has no ambient `trackId`); the hub
 * derives the track from the session row, mirroring the COP-094 consent surface
 * (`GET /approvals/grants`).
 */
export const PersonalSkillMineQuerySchema = Type.Object({ sessionId: UuidSchema }, { additionalProperties: false });
/**
 * Lean row for the management dropdown. The dropdown only lists + removes, so it
 * never ships `contentMarkdown` (up to 16 KB) — mirrors the lean
 * `ConsentGrantRowSchema`. Full markdown stays on `GET /skills/:id`.
 */
export const PersonalSkillMineRowSchema = Type.Object({
    id: UuidSchema,
    name: SkillNameSchema,
    description: SkillDescriptionSchema,
    slug: SkillSlugSchema,
}, { additionalProperties: false });
export const PersonalSkillMineListResponseSchema = Type.Object({ skills: Type.Array(PersonalSkillMineRowSchema) }, { additionalProperties: false });
/** Path params shared by admin shared-skill curation item routes. */
export const SharedSkillIdParamsSchema = Type.Object({ id: UuidSchema }, { additionalProperties: false });
const SharedSkillEditableFields = {
    name: SkillNameSchema,
    description: SkillDescriptionSchema,
    contentMarkdown: SkillContentMarkdownSchema,
    requiredCapabilities: SkillCapabilitiesSchema,
    optionalCapabilities: SkillCapabilitiesSchema,
};
const SharedSkillManagementFields = {
    id: UuidSchema,
    slug: SkillSlugSchema,
    ...SharedSkillEditableFields,
    status: SkillStatusSchema,
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
};
/**
 * Admin-curation DTO for shared deployment skills.
 *
 * Full markdown is present because this is a management/curation surface; the
 * user-visible loaded-skill SSE payload remains metadata-only below.
 */
export const SharedDeploymentSkillRowSchema = Type.Object({
    ...SharedSkillManagementFields,
    scope: Type.Literal('deployment'),
    trackId: Type.Null(),
    ownerUserId: Type.Null(),
}, { additionalProperties: false });
/** Admin-curation DTO for shared track skills. */
export const SharedTrackSkillRowSchema = Type.Object({
    ...SharedSkillManagementFields,
    scope: Type.Literal('track'),
    trackId: UuidSchema,
    ownerUserId: Type.Null(),
}, { additionalProperties: false });
export const SharedSkillRowSchema = Type.Union([
    SharedDeploymentSkillRowSchema,
    SharedTrackSkillRowSchema,
]);
export const SharedSkillResponseSchema = Type.Object({ skill: SharedSkillRowSchema }, { additionalProperties: false });
/**
 * Admin shared-skill create body.
 *
 * Direct `user_track` writes stay unavailable; normal-user personal changes
 * continue through exact-payload skill-suggestion approvals (COP-067).
 */
export const SharedSkillCreateBodySchema = Type.Union([
    Type.Object({
        scope: Type.Literal('deployment'),
        ...SharedSkillEditableFields,
    }, { additionalProperties: false }),
    Type.Object({
        scope: Type.Literal('track'),
        trackId: UuidSchema,
        ...SharedSkillEditableFields,
    }, { additionalProperties: false }),
]);
/** Admin shared-skill patch body; scope, owner, and track binding are immutable. */
export const SharedSkillUpdateBodySchema = Type.Object({
    name: Type.Optional(SkillNameSchema),
    description: Type.Optional(SkillDescriptionSchema),
    contentMarkdown: Type.Optional(SkillContentMarkdownSchema),
    requiredCapabilities: Type.Optional(SkillCapabilitiesSchema),
    optionalCapabilities: Type.Optional(SkillCapabilitiesSchema),
    status: Type.Optional(SkillStatusSchema),
}, { additionalProperties: false });
const SkillSuggestionFields = {
    kind: Type.Literal('skill_suggestion'),
    scope: Type.Literal('user_track'),
    trackId: UuidSchema,
    ownerUserId: UuidSchema,
    name: SkillNameSchema,
    description: SkillDescriptionSchema,
    contentMarkdown: SkillContentMarkdownSchema,
    requiredCapabilities: SkillCapabilitiesSchema,
    optionalCapabilities: SkillCapabilitiesSchema,
};
/**
 * Exact payload bound into `tf_approval` for Copilot-proposed personal skills.
 *
 * COP-067 requires normal-user suggestions to create/update only current
 * user×track personal skills. The approval hash is stored on the approval row;
 * this DTO is the bound payload whose bytes are hashed and later revalidated
 * before repository persistence.
 */
export const SkillSuggestionApprovalPayloadSchema = Type.Union([
    Type.Object({
        ...SkillSuggestionFields,
        operation: Type.Literal('create'),
    }, { additionalProperties: false }),
    Type.Object({
        ...SkillSuggestionFields,
        operation: Type.Literal('update'),
        targetSkillId: UuidSchema,
    }, { additionalProperties: false }),
]);
/**
 * Metadata-only SSE payload for `data-skill-loaded`.
 *
 * Full `contentMarkdown` is intentionally absent from this user-visible part;
 * COP-037/COP-070 store the exact loaded content on the trace/audit surface.
 */
export const SkillLoadedPartSchema = Type.Object({
    skillId: UuidSchema,
    name: Type.String({ minLength: 1, maxLength: 200 }),
    slug: Type.String({ minLength: 1, maxLength: 200 }),
    scope: SkillScopeSchema,
    trackId: Type.Union([UuidSchema, Type.Null()]),
    ownerUserId: Type.Union([UuidSchema, Type.Null()]),
}, { additionalProperties: false });
//# sourceMappingURL=skills.js.map